/**
 * HYCO Video review slider: tabs, Swiper carousels, progress bar,
 * and inline playback for Shopify-hosted MP4 (<video> element).
 */

(function () {
  'use strict';

  const DESKTOP_BREAKPOINT = 990;
  const DESKTOP_MEDIA_QUERY = '(min-width: ' + DESKTOP_BREAKPOINT + 'px)';

  function isDesktopViewport() {
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
  }

  /**
   * @param {*} swiper Swiper instance
   * @param {HTMLElement|null} prevEl
   * @param {HTMLElement|null} nextEl
   */
  function updateNavState(swiper, prevEl, nextEl) {
    if (!swiper || (!prevEl && !nextEl)) {
      return;
    }

    const locked = swiper.isLocked;
    const atStart = swiper.isBeginning;
    const atEnd = swiper.isEnd;

    if (prevEl) {
      prevEl.disabled = locked || atStart;
      prevEl.setAttribute(
        'aria-disabled',
        prevEl.disabled ? 'true' : 'false',
      );
      prevEl.classList.toggle(
        'hyco-vrs__nav-btn--inactive',
        prevEl.disabled,
      );
    }

    if (nextEl) {
      nextEl.disabled = locked || atEnd;
      nextEl.setAttribute(
        'aria-disabled',
        nextEl.disabled ? 'true' : 'false',
      );
      nextEl.classList.toggle(
        'hyco-vrs__nav-btn--inactive',
        nextEl.disabled,
      );
    }
  }

  /**
   * @param {HTMLElement} swiperEl
   * @returns {boolean}
   */
  function hasSlides(swiperEl) {
    const wrap = swiperEl.querySelector(':scope > .swiper-wrapper');

    return wrap ? wrap.children.length > 0 : false;
  }

  /**
   * @param {HTMLElement} slideZone
   */
  function destroySwiperInZone(slideZone) {
    const swiperEl = slideZone.querySelector('.swiper');

    if (swiperEl && swiperEl.swiper) {
      swiperEl.swiper.destroy(true, true);
    }

    slideZone.dataset.hycoSwiperMounted = '';
  }

  /**
   * @param {HTMLElement} root
   * @param {*} Swiper
   * @param {string} name
   * @returns {*|null}
   */
  function initSwiperInPanel(root, Swiper, name) {
    const slideZone = root.querySelector(
      '[data-hyco-vrs-panel="' + name + '"]',
    );

    if (!slideZone || slideZone.hidden) {
      return null;
    }

    const swiperEl = slideZone.querySelector('.swiper');
    const navPrev = slideZone.querySelector('.hyco-vrs__nav-prev');
    const navNext = slideZone.querySelector('.hyco-vrs__nav-next');
    const pagEl = slideZone.querySelector('.hyco-vrs__pagination');

    if (!swiperEl || !hasSlides(swiperEl)) {
      return null;
    }

    destroySwiperInZone(slideZone);

    const isVideo = name === 'video';

    if (!isVideo && !isDesktopViewport()) {
      slideZone.setAttribute('data-hyco-vrs-text-grid', '1');
      slideZone.dataset.hycoSwiperMounted = '';
      return null;
    }

    if (!isVideo) {
      slideZone.removeAttribute('data-hyco-vrs-text-grid');
    }

    const slidesPerViewMob = isVideo ? 1.4 : 1.08;
    const slidesPerViewDesk = isVideo ? 4.06 : 3.06;

    const swiper = new Swiper(swiperEl, {
      slidesPerView: slidesPerViewMob,
      spaceBetween: 8,
      speed: 450,
      watchSlidesProgress: true,
      navigation:
        navPrev && navNext
          ? {
              nextEl: navNext,
              prevEl: navPrev,
            }
          : false,
      pagination: pagEl
        ? {
            el: pagEl,
            type: 'progressbar',
          }
        : false,
      breakpoints: {
        [DESKTOP_BREAKPOINT]: {
          slidesPerView: slidesPerViewDesk,
          spaceBetween: 12,
        },
      },
      on: {
        afterInit(sw) {
          updateNavState(sw, navPrev, navNext);
        },
        slideChange(sw) {
          updateNavState(sw, navPrev, navNext);
        },
        resize(sw) {
          updateNavState(sw, navPrev, navNext);
        },
      },
    });

    slideZone.dataset.hycoSwiperMounted = '1';
    updateNavState(swiper, navPrev, navNext);

    return swiper;
  }

  /**
   * @param {HTMLElement} root
   */
  function destroyAllSwipers(root) {
    ['video', 'text'].forEach(function eachName(name) {
      const slideZone = root.querySelector(
        '[data-hyco-vrs-panel="' + name + '"]',
      );

      if (slideZone) {
        destroySwiperInZone(slideZone);
      }
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLVideoElement} activeVideo
   */
  function pauseOtherVideos(root, activeVideo) {
    root
      .querySelectorAll('[data-hyco-vrs-video]')
      .forEach(function pauseVideo(video) {
        if (video === activeVideo) {
          return;
        }

        const media = video.closest('.hyco-vrs-tile__media');

        video.pause();

        if (media) {
          media.removeAttribute('data-hyco-vrs-playing');
        }

        video.controls = false;
      });
  }

  /**
   * @param {HTMLElement} root
   */
  function bindInlinePlayback(root) {
    if (root.dataset.hycoVrsInlinePlaybackBound === '1') {
      return;
    }

    root.dataset.hycoVrsInlinePlaybackBound = '1';

    root.addEventListener('click', function onInlineVideoPlay(ev) {
      const opener = ev.target.closest('[data-hyco-vrs-video-play]');

      if (!opener || !root.contains(opener)) {
        return;
      }

      ev.preventDefault();

      const media = opener.closest('.hyco-vrs-tile__media');
      const video = media?.querySelector('[data-hyco-vrs-video]');

      if (!media || !video) {
        return;
      }

      pauseOtherVideos(root, video);

      video.controls = true;
      media.setAttribute('data-hyco-vrs-playing', 'true');

      video.play().catch(function onInlinePlayError() {
        media.removeAttribute('data-hyco-vrs-playing');
        video.controls = false;
      });
    });

    root
      .querySelectorAll('[data-hyco-vrs-video]')
      .forEach(function bindVideoState(video) {
        const media = video.closest('.hyco-vrs-tile__media');

        if (!media || video.dataset.hycoVrsVideoBound === '1') {
          return;
        }

        video.dataset.hycoVrsVideoBound = '1';

        video.addEventListener('play', function markPlaying() {
          media.setAttribute('data-hyco-vrs-playing', 'true');
        });

        video.addEventListener('ended', function markEnded() {
          media.removeAttribute('data-hyco-vrs-playing');
          video.controls = false;
        });
      });
  }

  /**
   * Sync tab panels and initialise Swiper for the visible tab.
   * @param {HTMLElement} root
   * @param {HTMLButtonElement} activeBtn
   * @param {*} SwiperCtor
   */
  function activateTabPanels(root, activeBtn, SwiperCtor) {
    const targetId = activeBtn.getAttribute('aria-controls') || '';

    root.querySelectorAll('[data-hyco-vrs-tab]').forEach(function mark(btn) {
      const isSelected =
        btn.getAttribute('aria-controls') === targetId;
      btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');

      btn.tabIndex = isSelected ? 0 : -1;
    });

    const panels = root.querySelectorAll('[role="tabpanel"]');

    panels.forEach(function togglePanel(panel) {
      const isMatch = !!panel.id && panel.id === targetId;
      panel.hidden = !isMatch;
      panel.setAttribute(
        'aria-hidden',
        isMatch ? 'false' : 'true',
      );
    });

    const panelName = activeBtn.getAttribute('data-hyco-vrs-tab');

    window.requestAnimationFrame(function afterReveal() {
      if (panelName) {
        initSwiperInPanel(root, SwiperCtor, panelName);
      }

      panels.forEach(function refreshVisible(panel) {
        if (panel.hidden) {
          return;
        }

        const sw = panel.querySelector('.swiper')?.swiper;

        if (sw) {
          sw.update();

          updateNavState(
            sw,
            panel.querySelector('.hyco-vrs__nav-prev'),
            panel.querySelector('.hyco-vrs__nav-next'),
          );
        }
      });
    });
  }

  /**
   * Delegated clicks for tab widgets (avoid duplicate listeners on reinits).
   * @param {HTMLElement} root
   * @param {*} SwiperCtor
   */
  function bindTabsDelegated(root, SwiperCtor) {
    if (root.dataset.hycoVrsTabsBound === '1') {
      return;
    }

    root.dataset.hycoVrsTabsBound = '1';

    root.addEventListener('click', function delegatedTabs(ev) {
      const btn = ev.target.closest('[data-hyco-vrs-tab]');

      if (!btn || !root.contains(btn)) {
        return;
      }

      ev.preventDefault();

      activateTabPanels(root, btn, SwiperCtor);
    });

    root.addEventListener(
      'keydown',
      function rovingTabs(ev) {
        const current = ev.target.closest('[data-hyco-vrs-tab]');

        if (
          !current ||
          !root.contains(current) ||
          (ev.key !== 'ArrowRight' && ev.key !== 'ArrowLeft')
        ) {
          return;
        }

        ev.preventDefault();

        const tabs = Array.from(root.querySelectorAll('[data-hyco-vrs-tab]'));
        const currentIndex = tabs.indexOf(current);
        let nextIndex =
          ev.key === 'ArrowRight'
            ? currentIndex + 1
            : currentIndex - 1;

        if (nextIndex < 0) {
          nextIndex = tabs.length - 1;
        }

        if (nextIndex >= tabs.length) {
          nextIndex = 0;
        }

        const candidate = tabs[nextIndex];

        if (candidate && candidate !== current) {
          candidate.focus();
          activateTabPanels(root, candidate, SwiperCtor);
        }
      },
    );
  }

  /**
   * @param {HTMLElement} root
   */
  function initRoot(root) {
    const SwiperCtor = window.Swiper;

    bindInlinePlayback(root);

    if (typeof SwiperCtor !== 'function') {
      return;
    }

    destroyAllSwipers(root);

    const tabButtons =
      root.querySelectorAll('[data-hyco-vrs-tab]');

    if (tabButtons.length >= 2) {
      bindTabsDelegated(root, SwiperCtor);

      let activeBtn = root.querySelector(
        '[data-hyco-vrs-tab][aria-selected="true"]',
      );

      if (!activeBtn) {
        activeBtn =
          tabButtons[0];

        activateTabPanels(root, activeBtn, SwiperCtor);

        return;
      }

      activateTabPanels(root, activeBtn, SwiperCtor);

      return;
    }

    delete root.dataset.hycoVrsTabsBound;

    const solePanel = root.querySelector('[data-hyco-vrs-panel]');

    const name =
      solePanel &&
      solePanel.getAttribute('data-hyco-vrs-panel');

    if (!solePanel || !name) {
      return;
    }

    solePanel.hidden = false;
    solePanel.setAttribute('aria-hidden', 'false');

    window.requestAnimationFrame(function mountSolePanel() {
      initSwiperInPanel(root, SwiperCtor, name);
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function refreshTextReviewLayout(root) {
    const SwiperCtor = window.Swiper;

    if (typeof SwiperCtor !== 'function') {
      return;
    }

    const textZone = root.querySelector('[data-hyco-vrs-panel="text"]');

    if (!textZone) {
      return;
    }

    const isMobile = !isDesktopViewport();

    if (isMobile) {
      destroySwiperInZone(textZone);

      if (!textZone.hidden) {
        textZone.setAttribute('data-hyco-vrs-text-grid', '1');
      }

      return;
    }

    textZone.removeAttribute('data-hyco-vrs-text-grid');

    if (textZone.hidden) {
      return;
    }

    const swiperEl = textZone.querySelector('.swiper');
    const existing = swiperEl ? swiperEl.swiper : null;

    if (!existing) {
      initSwiperInPanel(root, SwiperCtor, 'text');

      return;
    }

    existing.update();
  }

  let resizeTimer;

  function scheduleLayoutRefresh() {
    window.clearTimeout(resizeTimer);

    resizeTimer = window.setTimeout(function onResizeLayout() {
      document
        .querySelectorAll('[data-hyco-video-review-slider]')
        .forEach(refreshTextReviewLayout);
    }, 140);
  }

  function initEverySection() {
    document
      .querySelectorAll('[data-hyco-video-review-slider]')
      .forEach(initRoot);

    if (!window.__hycoVrsResizeBound) {
      window.__hycoVrsResizeBound = true;
      window.addEventListener('resize', scheduleLayoutRefresh);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEverySection);
  } else {
    initEverySection();
  }

  document.addEventListener(
    'shopify:section:load',
    function hydrateSection(ev) {
      const root = ev.target.querySelector(
        '[data-hyco-video-review-slider]',
      );

      if (root) {
        initRoot(root);
      }
    },
  );

  document.addEventListener(
    'shopify:section:unload',
    function teardownSection(ev) {
      const root = ev.target.querySelector(
        '[data-hyco-video-review-slider]',
      );

      if (!root) {
        return;
      }

      destroyAllSwipers(root);

      delete root.dataset.hycoVrsInlinePlaybackBound;
      delete root.dataset.hycoVrsTabsBound;
    },
  );
})();
